import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import { storeAuthToken, storeUserData } from '../../utils/security';
import { handleApiError } from '../../utils/errorHandler';
import axios from 'axios';
import toast from 'react-hot-toast';

const SocialCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();



  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const processToken = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const userDataParam = params.get('userData');
        
        if (!token) {
          setError('No authentication token found');
          toast.error('Authentication failed. Please try again.');
          navigate('/dashboard');
          return;
        }
        
        // Store token securely
        storeAuthToken(token);
 
        
        if (userDataParam) {
          try {
            // Parse the userData from the URL parameter
            const userData = JSON.parse(decodeURIComponent(userDataParam));
            storeUserData(userData);
            setLoading(false);
            toast.success('Successfully logged in!');
            navigate('/dashboard');
          } catch (parseError) {
            console.error('Error parsing user data:', parseError);
            // If userData parsing fails, fetch user data with the token
            await fetchUserData(token);
          }
        } else {
          // If no userData in URL, fetch it with the token
          await fetchUserData(token);
        }

        window.location.reload()
      } catch (error) {
        handleApiError(error);
        setError('Failed to process authentication');
        toast.error('Authentication failed. Please try again.');
        setLoading(false);
        navigate('/dashboard');
      }
    };
    
    const fetchUserData = async (token) => {
      try {
        // Set the default Authorization header for all axios requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/me`
        );

        if (response.data.success) {
          const userData = response.data.data;
          storeUserData(userData);
          setLoading(false);
          toast.success('Successfully logged in!');
          navigate('/dashboard');
        } else {
          throw new Error('Failed to fetch user data');
        }
      } catch (error) {
        handleApiError(error);
        setError('Failed to fetch user data');
        toast.error('Authentication failed. Please try again.');
        setLoading(false);
        navigate('/dashboard');
      }
    };
    
    processToken();
  }, [location, navigate]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
      <motion.div
        className="w-full max-w-md"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            {loading ? (
              <>
                <div className="loading-spinner mx-auto mb-4"></div>
                <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">Logging you in...</h2>
                <p className="text-gray-600 dark:text-gray-300">Please wait while we complete your authentication.</p>
              </>
            ) : error ? (
              <>
                <div className="text-red-500 text-5xl mb-4">⚠️</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">Authentication Error</h2>
                <p className="text-gray-600 dark:text-gray-300 mb-4">{error}</p>
                <button 
                  onClick={() => navigate('/login')} 
                  className="btn btn-primary"
                >
                  Return to Login
                </button>
              </>
            ) : null}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SocialCallback;